/**
 * Cycle empty screenplay blocks by type, independently of preceding blocks.
 * Tab advances the cycle; Shift-Tab reverses it. Tab also leads an empty End of
 * Act to New Act and an empty New Act into the cycle; Shift-Tab leaves them
 * unchanged. Dual dialogue falls through to subsequent keymaps.
 * Populated blocks and selections pass these keys on; when the next-block
 * plugin runs first, Tab at the end of a populated block adds one.
 */

import { keymap } from 'prosemirror-keymap'
import { type NodeType } from 'prosemirror-model'
import { type Command } from 'prosemirror-state'

import { setEditorBlockType } from '#util'
import { isEmptyBlock } from '#util/prosemirrorHelpers/wrapped-block-helpers'

/** Empty screenplay block cycling bound to Tab and Shift-Tab. */
export const emptyBlockCyclePlugin = keymap({
  Tab: cycleEmptyScreenplayBlock(false),
  'Shift-Tab': cycleEmptyScreenplayBlock(true),
})

function cycleEmptyScreenplayBlock(reverse: boolean): Command {
  return (state, dispatch) => {
    if (!state.selection.empty) return false

    const { $from } = state.selection
    const block = $from.parent
    const dual = $from.depth > 2

    if (dual) return false
    if (!isEmptyBlock(block)) return false

    const next = getNextEmptyScreenplayBlockType(block.type, reverse)
    if (next === block.type) return false

    return setEditorBlockType(next.name)(state, dispatch)
  }
}

function getNextEmptyScreenplayBlockType(
  blockType: NodeType,
  reverse: boolean,
) {
  const {
    sceneHeading,
    action,
    character,
    transition,
    dialogue,
    parenthetical,
    new_act,
    end_of_act,
  } = blockType.schema.nodes

  // Acts lead into the cycle on Tab, but aren't part of it
  if (blockType === end_of_act) return reverse ? blockType : new_act
  if (blockType === new_act) return reverse ? blockType : sceneHeading

  const cycles = [
    [sceneHeading, action, character, transition],
    [dialogue, parenthetical],
  ]
  const cycle = cycles.find((types) => types.includes(blockType))
  if (!cycle) return blockType
  const index = cycle.indexOf(blockType)
  return cycle[(index + (reverse ? -1 : 1) + cycle.length) % cycle.length]
}
